import React, { useState, useEffect } from 'react';
import { X, Search, Warehouse, Save, Loader2, MapPin, ArrowRightLeft, Settings, DollarSign, Calculator, Lock } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import UniversalLookup from './UniversalLookup';
import { useTranslation } from 'react-i18next';

const AddToolToStoreModal = ({ isOpen, onClose, toolnum, onRefresh }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [storeData, setStoreData] = useState({
    location: '',
    siteid: '',
    issueunit: '',
    curbal: 0,
    avgcost: 0
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {} });
  const [loading, setLoading] = useState(false);

  const totalValue = (Number(storeData.curbal) * Number(storeData.avgcost)).toFixed(2);

  useEffect(() => {
    if (isOpen && toolnum) {
      const fetchToolDetails = async () => {
        try {
          // جلب بيانات الأداة من جدول الأدوات الجديد
          const res = await axios.get(`/api/tools/${toolnum}`);
          setStoreData(prev => ({
            ...prev,
            issueunit: res.data.issueunit || 'EACH'
          }));
        } catch (err) { console.error("Error fetching tool units:", err); }
      };
      fetchToolDetails();
    } else {
      setStoreData({ location: '', siteid: '', issueunit: '', curbal: 0, avgcost: 0 });
    }
  }, [isOpen, toolnum]);

  if (!isOpen) return null;

  const handleAssign = async () => {
    if (!storeData.location) return toast.error(t('addToolToStoreModal.selectStoreroom'));
    setLoading(true);
    try {
      // إرسال البيانات للباكيند (نفس الـ Endpoint أو واحد مخصص للأدوات)
      const token = localStorage.getItem('token');
      await axios.post('/api/tool/inventory/assign', {
        toolnum: toolnum,
        ...storeData,
        curbal: Number(storeData.curbal),
        avgcost: Number(storeData.avgcost)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('addToolToStoreModal.initSuccess', { toolnum, location: storeData.location }));
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('addToolToStoreModal.initFailed'));
    } finally { setLoading(false); }
  };

  return (
    <div className={`fixed inset-0 z-[5000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200 uppercase tracking-tighter ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-[550px] shadow-2xl rounded-sm overflow-hidden border border-line animate-in zoom-in-95">

        {/* Header - Orange Theme for Tools */}
        <div className="bg-primary-dark text-ink-inverse p-4 flex justify-between items-center border-b-4 border-b-accent">
          <div className="flex items-center gap-3">
            <Settings size={18} className="text-accent" />
            <span className="text-[11px] font-black tracking-widest italic">{t('addToolToStoreModal.title')}</span>
          </div>
          <button onClick={onClose} className="hover:bg-status-danger p-1 rounded-full transition-colors text-ink-inverse"><X size={18}/></button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 bg-surface-panel/50">

          <div className={`flex items-center justify-between border-b border-line pb-3 bg-surface-card p-3 rounded shadow-sm border-accent font-black ${isRTL ? 'border-r-4' : 'border-l-4'}`}>
              <div className="flex flex-col">
                <label className="text-[8px] text-ink-secondary italic">{t('addToolToStoreModal.toolId')}</label>
                <span className="text-primary text-sm">{toolnum}</span>
              </div>
              <Settings size={20} className="text-ink-secondary/30" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1 border-b border-line focus-within:border-accent transition-all">
                <label className="text-[9px] font-black text-ink-secondary uppercase">{t('addToolToStoreModal.storeroomLocation')}</label>
                <div className="flex items-center justify-between py-1">
                    <span className={`font-black text-[11px] ${storeData.location ? 'text-primary' : 'text-ink-secondary/60 italic'}`}>{storeData.location || t('addToolToStoreModal.search')}</span>
                    <button onClick={() => setLookup({ isOpen: true, title: t('addToolToStoreModal.storerooms'), type: 'storerooms', field: 'location' })}>
                        <Search size={14} className="text-accent hover:scale-110 transition-transform" />
                    </button>
                </div>
            </div>
            <div className="flex flex-col gap-1 border-b border-line-subtle bg-surface-panel/50 p-1 rounded opacity-80">
                <label className="text-[9px] font-black text-ink-secondary uppercase flex items-center gap-2"><Lock size={10}/> {t('addToolToStoreModal.siteId')}</label>
                <span className="font-black text-ink-secondary text-[11px] py-1">{storeData.siteid || '---'}</span>
            </div>
          </div>

          {/* Valuation Section */}
          <div className="bg-surface-card p-5 border rounded-sm shadow-sm space-y-5 border-t-4 border-t-status-success">
             <div className="flex items-center gap-2 text-status-success font-black mb-2 uppercase text-[9px]">
                <DollarSign size={14}/> {t('addToolToStoreModal.openingValuation')}
             </div>
             <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col border-b border-line">
                    <label className="text-[8px] font-black text-ink-secondary uppercase">{t('addToolToStoreModal.openingQty')}</label>
                    <input type="number" className="font-black text-primary outline-none py-2 bg-transparent text-lg font-mono" value={storeData.curbal} onChange={(e) => setStoreData({...storeData, curbal: e.target.value})} />
                </div>
                <div className={`flex flex-col border-b border-line ${isRTL ? 'text-left' : 'text-right'}`}>
                    <label className="text-[8px] font-black text-ink-secondary uppercase">{t('addToolToStoreModal.unitCost')}</label>
                    <input type="number" className={`font-black text-primary outline-none py-2 bg-transparent text-lg font-mono ${isRTL ? 'text-left' : 'text-right'}`} value={storeData.avgcost} onChange={(e) => setStoreData({...storeData, avgcost: e.target.value})} />
                </div>
             </div>
             <div className="bg-status-success-bg p-3 rounded flex justify-between items-center border border-status-success/20">
                <span className="text-[9px] font-black text-status-success uppercase italic">{t('addToolToStoreModal.totalValue')}</span>
                <span className="text-xl font-black text-status-success font-mono tracking-tighter">{totalValue} EGP</span>
             </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-surface-panel border-t flex justify-end gap-3 px-8">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-black text-ink-secondary uppercase">{t('common.cancel', 'Cancel')}</button>
          <button onClick={handleAssign} disabled={loading} className="bg-primary-dark hover:bg-accent-hover text-ink-inverse px-8 py-2 rounded-sm font-black flex items-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50 uppercase tracking-widest">
            {loading ? <Loader2 className="animate-spin" size={14}/> : <Save size={14} />}
            {t('addToolToStoreModal.finalize')}
          </button>
        </div>

        <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })}
          onSelect={(row) => {
            const locValue = row.location || row.location_id;
            const siteValue = row.site_id || row.siteid;
            if (lookup.field === 'location') {
                setStoreData(prev => ({ ...prev, location: locValue, siteid: siteValue }));
            }
            setLookup({ ...lookup, isOpen: false });
          }}
        />
      </div>
    </div>
  );
};

export default AddToolToStoreModal;
